<template>
    <div id="subjects">
        <supsis-table
            :columns="cols()"
            :rows="getFilteredRows()"
            :bottom-pagination="true"
            :pagination-cursor="paginationCursor"
            @cursor-changed="(cursor) => (paginationCursor = cursor)"
        >
            <template #header>
                <div class="d-flex justify-content-between align-items-center">
                    <a-input-search
                        v-model="searchText"
                        :placeholder="$t('message.search')"
                        class="search-input"
                        @search="onSearch"
                    />
                    <SupsisButton
                        ghost
                        @click="$feature.beforeCreateDepartment(showModalWithoutEdit, paginationCursor.total)"
                    >
                        <md-icon>add</md-icon>
                        {{ $t("message.createDepartment") }}
                    </SupsisButton>
                </div>
            </template>
        </supsis-table>
        <Modal2
            v-if="isModalShow"
            id="subject-form"
            :title="editMode ? $t('message.updateDepartmentForm') : $t('message.newDepartmentForm')"
            @cancel="hideModal"
            :show-footer="false"
        >
            <div id="modal-inner">
                <a-alert
                    v-if="$validator.errors.items.length && submitClicked"
                    :key="`alertContainer-${$i18n.locale}`"
                    :message="$t('message.error')"
                    class="mb-2"
                    showIcon
                    type="error"
                >
                    <div slot="description">
                        <div v-for="err of $validator.errors.items">
                            <span>
                                {{ err.msg }}
                            </span>
                        </div>
                    </div>
                </a-alert>
                <b-form class="d-flex flex-column justify-content-center" @submit.prevent="duplicateControl">
                    <b-row>
                        <b-col md="12">
                            <b-row>
                                <b-col md="12">
                                    <MultiContent v-model="subjectInitialObject" ref="MultiContent" />
                                </b-col>
                                <b-col class="mt-3" md="12">
                                    <b-form-group :label="$t('report.status')">
                                        <b-form-radio-group v-model="subject.isActive" name="status" required>
                                            <b-form-radio :value="true">{{ $t("message.active") }}</b-form-radio>
                                            <b-form-radio :value="false">{{ $t("message.passive") }}</b-form-radio>
                                        </b-form-radio-group>
                                    </b-form-group>
                                </b-col>
                                <b-col md="12">
                                    <b-button
                                        :disabled="loading"
                                        type="submit"
                                        variant="primary"
                                        v-if="checkPermission(PERMISSION_TYPES['topic.update'].tr.name)"
                                    >
                                        <template v-if="!loading">
                                            <span v-if="!editMode">
                                                {{ $t("message.save") }}
                                            </span>
                                            <span v-else>
                                                {{ $t("message.update") }}
                                            </span>
                                        </template>
                                        <template v-else>
                                            <font-awesome-icon icon="spinner" spin />
                                        </template>
                                    </b-button>
                                    <b-button @click="hideModal">
                                        <template>
                                            {{ $t("message.cancel") }}
                                        </template>
                                    </b-button>
                                </b-col>
                            </b-row>
                        </b-col>
                    </b-row>
                </b-form>
            </div>
        </Modal2>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faPencilAlt, faPlusCircle, faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";
import { log2 } from "../../../../../utils/log2";
import * as _ from "lodash";
import { sendSocketMessage } from "../../../../../utils/socketStream";
import ActionEdit from "../../../../../components/svg/ActionEdit.vue";
import ActionDelete from "../../../../../components/svg/ActionDelete.vue";
import Pagination from "../../../../../components/Pagination.vue";
import { toArray2, toObject2 } from "../../../../../utils/languageContent";
import MultiContent from "../../../../../components/MultiContent.vue";
import Modal2 from "../../../../../components/common/Modal2.vue";
import { PERMISSION_TYPES } from "../../../../../language/permissionTypes";
import SupsisTable from "../../../../../components/SupsisTable.vue";
import SupsisButton from "../../components/SupsisButton.vue";
import CreateButton from "../../../../../components/CreateButton.vue";
import { SanitizeHtml } from "../../../../../utils/utils";

library.add(faPencilAlt, faTimes, faPlusCircle, faSpinner);
const log = log2("Subjects");
const subjectInitialObject = {
    _id: null,
    languageContent: {
        tr: {
            name: "",
            description: "",
        },
        en: {
            name: "",
            description: "",
        },
        ru: {
            name: "",
            description: "",
        },
        fr: {
            name: "",
            description: "",
        },
        es: {
            name: "",
            description: "",
        },
        ar: {
            name: "",
            description: "",
        },
        it: {
            name: "",
            description: "",
        },
    },
    isActive: true,
};
export default {
    name: "Subjects",
    components: {
        CreateButton,
        SupsisButton,
        SupsisTable,
        MultiContent,
        ActionEdit,
        ActionDelete,
        Pagination,
        Modal2,
    },
    data() {
        return {
            editID: null,
            isBusy: true,
            loading: false,
            editMode: false,
            subject: _.cloneDeep(subjectInitialObject),
            submitClicked: false,
            searchText: undefined,
            cols: () => [
                {
                    field: "name",
                    key: "name",
                    title: this.$t("message.departmentName"),
                    renderBodyCell: ({ row }) => {
                        return row.languageContent[this.$i18n.locale].name;
                    },
                },
                {
                    field: "description",
                    key: "description",
                    title: this.$t("message.description"),
                    renderBodyCell: ({ row }) => {
                        return SanitizeHtml(row.languageContent[this.$i18n.locale].description, true);
                    },
                },
                {
                    field: "enable",
                    key: "enable",
                    title: this.$t("message.status"),
                    renderBodyCell: ({ row }) => {
                        return row.isActive ? (
                            <div class="material-icons text-success">check</div>
                        ) : (
                            <div class="material-icons text-danger">close</div>
                        );
                    },
                },
                {
                    align: "right",
                    field: "actionsSlot",
                    key: "actionsSlot",
                    title: this.$t("message.transactions"),
                    fixed: "right",
                    renderBodyCell: ({ row }) => {
                        const renderEditButton = () => {
                            return (
                                this.checkPermission(PERMISSION_TYPES["topic.update"].tr.name) && (
                                    <a-button
                                        class="actionButton d-flex align-items-center"
                                        on-click={() => this.edit(row._id)}
                                    >
                                        <span class="material-icons" style="color:#1bc5bd;">edit</span>
                                    </a-button>
                                )
                            );
                        };
                        const renderDeleteButton = () => {
                            return (
                                this.checkPermission(PERMISSION_TYPES["topic.delete"].tr.name) && (
                                    <a-button
                                        class="actionButton d-flex align-items-center"
                                        on-click={() => this.remove(row._id)}
                                    >
                                        <ActionDelete />
                                    </a-button>
                                )
                            );
                        };
                        return (
                            <div class="d-flex w-100 justify-content-end">
                                {renderEditButton()}
                                {renderDeleteButton()}
                            </div>
                        );
                    },
                },
            ],
            currentSort: {
                columnKey: "enable",
                order: "ascend",
            },
            subjectTitle: "",
            isModalShow: false,
            queryAction: this.$route.query.action,
            paginationCursor: {
                total: 0,
                page: 1,
                perPage: 10,
            },
        };
    },
    computed: {
        PERMISSION_TYPES() {
            return PERMISSION_TYPES;
        },
        ...mapState({
            subjects: (state) => state.subject.subjects,
            languages: (state) => state.language.languages,
            site: (state) => state.site.site,
            isAdmin: (state) => state.auth.adminUser != null,
            defaultLang: (state) => state.setting.settings.language.clientDefault,
            systemLanguages: (state) => state.setting.settings.systemLanguages,
        }),
        ...mapGetters({
            undeleted: "subject/getUndeleted",
            isDarkMode: "user/isDarkMode",
            checkPermission: "auth/checkPermission",
        }),
        subjectInitialObject: {
            get() {
                if (!this.systemLanguages) {
                    return [];
                }
                return [
                    {
                        type: "input",
                        field: "name",
                        label: this.$t("message.title"),
                        placeholder: this.$t("message.title"),
                        data: this.systemLanguages.reduce((acc, lang) => {
                            acc[lang] = _.get(this.subject.languageContent, `[${lang}].name`, "");
                            return acc;
                        }, {}),
                        validation: "required",
                    },
                    {
                        type: "rich",
                        field: "description",
                        label: this.$t("message.message"),
                        placeholder: this.$t("message.message"),
                        data: this.systemLanguages.reduce((acc, lang) => {
                            acc[lang] = _.get(this.subject.languageContent, `[${lang}].description`, "");
                            return acc;
                        }, {}),
                        validation: "required",
                    },
                ];
            },
            set(v) {
                this.subject.languageContent = this.systemLanguages.reduce((acc, lang) => {
                    acc[lang] = {};
                    for (const _v of v) acc[lang][_v.field] = _v.data[lang];
                    return acc;
                }, {});
            },
        },

        subjectInitialObjectTitle: {
            get() {
                return toArray2(this.subject, "languageContent", "name").languageContent;
            },
            set(value) {
                this.subject.languageContent = toObject2(value, "name");
            },
        },
        subjectInitialObjectContent: {
            get() {
                return toArray2(this.subject, "languageContent", "description").languageContent;
            },
            set(value) {
                this.subject.languageContent = toObject2(value, "description");
                this.addNewSubjectTitle();
            },
        },
    },
    methods: {
        ...mapActions({
            getSubjects: "subject/getSubjects",
            getSubject: "subject/getSubject",
            createSubject: "subject/createSubject",
            updateSubject: "subject/updateSubject",
            deleteSubject: "subject/deleteSubject",
        }),
        showModal() {
            this.isModalShow = true;
        },
        hideModal() {
            this.isModalShow = false;
            this.reset();
        },
        showModalWithoutEdit() {
            this.editMode = false;
            this.showModal();
        },
        addNewSubjectTitle() {
            Object.keys(this.subjectTitle).forEach((key) => {
                this.subject.languageContent[key].name = this.subjectTitle[key];
            });
        },

        getFilteredRows() {
            const items = this.searchText
                ? this.undeleted.filter((x) =>
                      x.languageContent[this.$i18n.locale].name
                          .toLocaleLowerCase("tr-TR")
                          .includes(this.searchText.toLocaleLowerCase("tr-TR"))
                  )
                : this.undeleted;
            return items;
        },
        onSearch(value) {
            log("onSearch", value);
            this.searchText = value;
        },
        get() {
            this.isBusy = true;
            this.getSubjects({ perPage: this.paginationCursor.perPage, page: this.paginationCursor.page }).then(
                (response) => {
                    this.paginationCursor.total = response.data.total;
                    this.isBusy = false;
                    if (this.queryAction == "create") this.isModalShow = true;
                }
            );
        },
        edit(id) {
            this.getSubject(id).then((response) => {
                this.subject = _.cloneDeep(response);
                this.editMode = true;
                this.editID = id;
                /*this.$bvModal.show("subject-form");*/
                this.showModal();
            });
        },
        publishNewSubjects() {
            const socketMessageId = "user publish-subjects";
            sendSocketMessage(socketMessageId, {
                siteId: this.site._id,
            });
        },
        remove(id) {
            this.$dialog
                .confirm(this.$t("message.deleteDepartmentQuestion"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.deleteSubject(id)
                        .then((x) => {
                            this.$toasted.show(this.$t("message.departmentDeleted"), {
                                duration: 3000,
                                position: "bottom-center",
                            });
                            const socketMessageId = this.isAdmin ? "admin subject-deleted" : "user subject-deleted";
                            sendSocketMessage(socketMessageId, {
                                subjectId: id,
                                siteId: this.site._id,
                            });
                            this.publishNewSubjects();
                        })
                        .catch((err) => {
                            log.error(err.response, err.message);
                            const errMsg =
                                err.response && err.response.data && err.response.data.localizedMessages
                                    ? err.response.data.localizedMessages[this.$i18n.locale]
                                    : err.message;
                            this.$toasted.error(errMsg, {
                                duration: 3000,
                                position: "bottom-center",
                            });
                        });
                });
        },
        reset() {
            log.debug("reset called");
            this.subject = _.cloneDeep(subjectInitialObject);
            this.submitClicked = false;
        },
        duplicateControl() {
            const isThereSameSubject = this.subjects.data.find(
                (x) =>
                    x.languageContent[this.$i18n.locale].name.toLowerCase() ===
                    this.subject.languageContent[this.$i18n.locale].name.toLowerCase()
            );
            const duplicateId = isThereSameSubject ? isThereSameSubject._id : null;
            const isThisSameSubject = duplicateId === this.editID;
            if (!isThisSameSubject && duplicateId !== null) {
                this.$toasted.error(this.$t("message.sameDepartment"), {
                    duration: 3000,
                    position: "bottom-center",
                });
                return;
            } else {
                this.handleSave();
            }
        },
        handleSave() {
            this.submitClicked = true;
            this.$validator.validateAll().then((response) => {
                if (response) {
                    this.loading = true;
                    Object.keys(this.subject.languageContent).forEach((key) => {
                        if (!this.subject.languageContent[key].name) {
                            this.subject.languageContent[key].description =
                                this.subject.languageContent[this.defaultLang].description;
                            this.subject.languageContent[key].name =
                                this.subject.languageContent[this.defaultLang].name;
                        }
                    });
                    const result = this.subject._id
                        ? this.updateSubject(this.subject)
                        : this.createSubject(this.subject);

                    result
                        .then(() => {
                            /*this.$bvModal.hide("subject-form");*/
                            this.hideModal();
                            this.$toasted.show(this.$t("message.departmentSaved"), {
                                duration: 3000,
                                position: "bottom-center",
                            });
                            this.get();
                            this.reset();
                            this.publishNewSubjects();
                            sendSocketMessage("team refresh-chats", {
                                siteId: this.site._id,
                            });
                        })
                        .finally(() => {
                            this.loading = false;
                            this.submitClicked = false;
                        });
                } else {
                    log("validation error", response, this.$validator);
                    this.loading = false;
                }
            });
        },
    },
    mounted() {
        this.get();
    },
    updated() {
        if (this.$validator.locale !== this.$i18n.locale) this.$validator.localize(this.$i18n.locale);
    },
    watch: {
        paginationCursor: {
            deep: true,
            handler() {
                this.get();
            },
        },
    },
};
</script>

<style lang="scss">
#subjects {
    position: relative;
    height: 100%;
    .fab {
        width: 50px;
        height: 50px;
        border-radius: 25px;
        background-color: white;
        position: absolute;
        bottom: 20px;
        z-index: 9999;
        transition: all 0.3s;
        box-shadow: 0 0 0 0 rgba(100, 100, 100, 1);
        transform: scale(1);
        animation: pulse 3s infinite;
        cursor: pointer;
    }

    @keyframes pulse {
        0% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(100, 100, 100, 0.5);
        }

        70% {
            transform: scale(1);
            box-shadow: 0 0 0 10px #00000000;
        }

        100% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 #00000000;
        }
    }

    #subject-form {
        #modal-inner {
            overflow-y: scroll;
            overflow-x: hidden;
            max-height: 70vh;
            padding: 0 20px;
        }

        .ant-modal-wrap {
            max-height: 100vh;
        }

        .ant-modal {
            width: 700px !important;
            padding-bottom: 0;
            top: 40px;
        }
    }
}
</style>
